import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { Notification, User } from "../models";
import { buildCommunityPostPath } from "../models/community.model";
import { profileDisplayName } from "../utils/avatar.utils";
import { HelperService } from "./helper.service";
import { UserService } from "./user.service";
import { environment } from "../environments/environment";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private toEmailFileRefs(files: any[] = []) {
    return files
      .filter((file) => file?.filename || file?.storage_id)
      .map((file) => ({
        filename: file.filename,
        storage_id: file.storage_id,
        storage_location: file.storage_location,
        user_id: file.user_id,
        type: file.type,
        thread_id: file.thread_id,
        mimetype: file.mimetype,
      }));
  }

  constructor(
    private http: HttpClient,
    private helperService: HelperService,
    private userService: UserService
  ) {}

  /**
   * Base URL for links inside notifications. Per-environment (dev/stage/prod), trailing
   * slash included. Wrapped in a getter so future multi-community routing can compose
   * `${brandBaseUrl}community/{communityId}/...` in one place.
   */
  private get brandBaseUrl(): string {
    return environment.fenderUrl;
  }

  /**
   * Creates a collaboration message notification
   * Handles: collaboration_new_message, collaboration_file_new_message, collaboration_file_new_waveform_comment
   */
  createCollaborationMessage(
    type: "collaboration_new_message" | "collaboration_file_new_message" | "collaboration_file_new_waveform_comment",
    user: User,
    message: string,
    workspace: any,
    file?: any,
    displayTime?: string
  ): Notification {
    let title: string;
    let url: string;
    let button: string;
    let body: string;

    if (type === "collaboration_file_new_waveform_comment") {
      title = `${user.firstName} added a timestamped comment to ${file.filename}`;
      body = `Comment at ${displayTime}: ${this.helperService.htmlToText(message)}`;
      url = `${this.brandBaseUrl}workspaces/file/${file.id}`;
      button = "View File";
    } else if (type === "collaboration_file_new_message") {
      title = `${user.firstName} about ${file.filename}`;
      body = this.helperService.htmlToText(message);
      url = `${this.brandBaseUrl}workspaces/file/${file.id}`;
      button = "View File";
    } else {
      title = `${user.firstName} in ${workspace.name}`;
      body = this.helperService.htmlToText(message);
      url = `${this.brandBaseUrl}workspaces/detail/${workspace.id || workspace.name}`;
      button = "View Workspace";
    }

    const payload: any = {
      htmlBody: message,
      first_name: user.firstName,
      last_name: user.lastName,
      workspace_name: workspace.name,
      workspace_id: workspace.id,
      workspace_id_with_prefix: "workspace_" + workspace.id,
    };

    if (file) {
      payload.file_name = file.filename;
      payload.file_id = file.id;
    }

    return {
      title,
      body,
      photo: user.photoURL,
      topics: ["collaboration"],
      url,
      button,
      type,
      payload,
    };
  }

  /**
   * Creates a community post notification
   * Handles: community_post_reply, community_post_mention, community_pinned_post_reply, community_post_liked
   */
  createCommunityPostNotification(
    type: "community_post_reply" | "community_post_mention" | "community_pinned_post_reply" | "community_post_liked",
    user: User,
    post: any,
    reply?: any,
    originalPost?: any,
    reactionType: string = "like",
    communityStringId?: string,
    betaStringId?: string
  ): Notification {
    let title: string;
    let body: string;
    // Prefer the post's own beta/community context (source of truth). This lets beta post
    // replies build a `beta/betapost/:id?betaStringId=…` URL from the same code path as
    // community posts — callers no longer need to hand-override notification.url. Falls
    // back to caller-supplied ids, then FSP default via the helper.
    const resolvedCommunity = post?.community_string_id || originalPost?.community_string_id || communityStringId;
    const resolvedBeta = betaStringId || post?.beta_string_id || originalPost?.beta_string_id;
    // The link and `primary_post_id` name the THREAD: the top-level post. `post` is the reply
    // itself for a like, a reaction or a mention inside a reply — keyed to that, the notification
    // opened the reply as though it were a post of its own. Callers pass the top-level post as
    // `originalPost` in exactly those cases; `reply_id` keeps which reply it was about.
    const postId = post?.id || post;
    const threadId = originalPost?.id || postId;
    // A reply notification's `post` is the thread and the new reply rides along as `reply`.
    const replyId = `${threadId}` !== `${postId}` ? postId : reply?.id;
    // `?reply=` in the link too, not only the payload: the email's button is just this URL, and
    // post detail scrolls to that reply — otherwise the member lands at the top of the thread.
    const url = this.brandBaseUrl + buildCommunityPostPath({ postId: threadId, communityStringId: resolvedCommunity, betaStringId: resolvedBeta, replyId: replyId || undefined });
    const button = "View Post";

    const payload: any = {
      primary_post_id: threadId,
    };
    if (replyId) payload.reply_id = replyId;
    if (post?.files?.length) {
      payload.fileRefs = this.toEmailFileRefs(post.files);
    }

    switch (type) {
      case "community_post_reply":
        title = `${user.firstName} replied to your post.`;
        body = reply ? this.helperService.htmlToText(reply.message) : this.helperService.htmlToText(post.message);
        payload.htmlBody = reply ? reply.message : post.message;
        payload.original_post = {
          id: originalPost?.id || post.id || post,
          message: originalPost ? this.helperService.htmlToText(originalPost.message) : this.helperService.htmlToText(post.message),
          author: originalPost?.author || post.author,
        };
        if (reply) {
          payload.reply = {
            author: reply.author,
            message: this.helperService.htmlToText(reply.message),
          };
        }
        break;

      case "community_pinned_post_reply":
        title = `${user.firstName} commented on a post you saved.`;
        body = reply ? reply.message : post.message;
        payload.original_post = {
          id: originalPost?.id || post.id || post,
          message: originalPost?.message || post.message,
          author: originalPost?.author || post.author,
        };
        if (reply) {
          payload.reply = {
            author: reply.author,
            message: reply.message,
          };
        }
        break;

      case "community_post_mention":
        title = `${user.firstName} mentioned you in a post.`;
        body = this.helperService.htmlToText(post.message);
        payload.htmlBody = post.message;
        payload.post = {
          id: post.id,
          message: post.message,
          author: post.author,
        };
        if (originalPost) {
          payload.original_post = originalPost;
        }
        break;

      case "community_post_liked": {
        const verb = reactionType && reactionType !== "like" ? `reacted with ${reactionType} to` : "liked";
        // Either profile shape — a name-only member used to send "undefined undefined liked…".
        const reactorName = profileDisplayName(user) || "Someone";
        title = `${reactorName} ${verb} your post in Community.`;
        body = `${reactorName} ${verb} "${this.helperService.htmlToText(post.message)}"`;
        payload.post = {
          id: post.id,
          message: this.helperService.htmlToText(post.message),
          author: post.author,
        };
        payload.likedBy = {
          name: reactorName,
          id: user.id,
          photo: user.photoURL,
        };
        payload.reactionType = reactionType || "like";
        if (originalPost) {
          payload.original_post = {
            id: originalPost.id,
            message: this.helperService.htmlToText(originalPost.message),
            author: originalPost.author,
          };
        }
        break;
      }
    }

    // Per-community topic (DENG-733) — put the specific community topic FIRST so the
    // paeapi resolver picks up the user's per-community channel prefs, then fall back
    // to the legacy `community` key (which resolveNotificationChannels uses as the FSP
    // backward-compat path). Beta posts stay outside this pattern; they carry beta
    // notifications on a separate path (see `createNewBetaPost`).
    const topics: string[] = [];
    if (resolvedCommunity && !resolvedBeta) topics.push(`communities.${resolvedCommunity}`);
    topics.push("community");

    return {
      title,
      body,
      photo: user.photoURL,
      topics,
      url,
      button,
      type,
      payload,
    };
  }

  /**
   * Creates a community direct message notification
   */
  createCommunityDirectMessage(user: User, messageObj: any): Notification {
    return {
      title: `${user.firstName} sent you a direct message.`,
      body: this.helperService.htmlToText(messageObj.message),
      photo: user.photoURL,
      topics: ["community"],
      type: "community_direct_message",
      url: `${this.brandBaseUrl}messages/${user.id}`,
      button: "View Message",
      payload: {
        htmlBody: messageObj.message,
        ...messageObj,
        message: this.helperService.htmlToText(messageObj.message),
      },
    };
  }

  /**
   * Creates a community connection invite notification
   */
  createCommunityConnectionInvite(user: User, connection: any, body: string): Notification {
    return {
      title: `${user.firstName} ${user.lastName} has invited you to connect in Studio One Pro+ Community!`,
      body: this.helperService.htmlToText(body),
      photo: user.photoURL,
      topics: ["community"],
      type: "community_connection_invite",
      url: `${this.brandBaseUrl}profile/${connection.requested_user_id}/connections?reviewConnectionFor=${user.id}`,
      button: "Review Invite",
      payload: {
        htmlBody: body,
        requested_by: user.id,
      },
    };
  }

  /**
   * Creates a new beta post notification
   */
  createNewBetaPost(user: User, post: any, betaStringId: string | boolean, betaProgramName?: string): Notification {
    const programName = betaProgramName || (typeof betaStringId === "string" ? betaStringId : "");
    return {
      title: `${user.firstName} added a post in the ${programName} beta program. - ${post.subject}`,
      body: `${this.helperService.htmlToText(post.message)}`,
      photo: user.photoURL,
      topics: ["beta_community"],
      type: "new_beta_post",
      url: this.brandBaseUrl + buildCommunityPostPath({ postId: post.id, betaStringId: typeof betaStringId === "string" ? betaStringId : undefined }),
      button: "View Post",
      payload: {
        htmlBody: post.message,
        post: {
          id: post.id,
          message: this.helperService.htmlToText(post.message),
          author: post.author,
        },
        primary_post_id: post.id,
        // The program this post belongs to. Beta posts live in their own Firestore collection,
        // so a client that only has a post id cannot tell which program — or even that it is a
        // beta post — without this. The mobile app reads it to open the right screen.
        beta_string_id: typeof betaStringId === "string" ? betaStringId : undefined,
        fileRefs: this.toEmailFileRefs(post.files || []),
      },
    };
  }

  /**
   * Creates a collaboration workspace deleted notification
   */
  createCollaborationWorkspaceDeleted(workspace: any): Notification {
    return {
      title: `${workspace.name} was deleted.`,
      body: `The workspace ${workspace.name} was deleted. Contact the workspace owner for more information.`,
      photo: workspace.image?.url || workspace.image,
      topics: ["collaboration"],
      type: "collaboration_workspace_deleted",
      payload: {
        workspace_name: workspace.name,
        workspace_id: workspace.id,
        workspace_id_with_prefix: "workspace_" + workspace.id,
      },
    };
  }

  /**
   * Sends a notification to the backend
   * Moved from UserService
   */
  sendNotification(data: { notification: Notification; user_ids: Array<number>; clients?: Array<"rollup" | "email" | "mobile" | "email_if_no_push"> }): Observable<any> {
    return this.http.post(environment.paeApiUrl + "user/send-notification", JSON.stringify(data), this.helperService.getHttpOptions());
  }
}
